import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
  Optional
} from '@angular/core';
import { Observable } from 'rxjs';

export interface IFilter {
  field: string;
  operator: string;
  value: any;
}

export interface ISort {
  field: string;
  type: 'ASC' | 'DESC';
}

export interface IRootConfig {
  domains: {
    [name: string]: {
      url: string;
      isDefault?: boolean;
    };
  };
}

export const SUPER_HTTP_CONFIG = new InjectionToken<IRootConfig>(
  'SUPER_HTTP_CONFIG'
);

export class HttpCriteria {
  _filters: IFilter[] = [];
  _sort: ISort[] = [];
  _perPage: number = 15;
  _page: number = 1;

  constructor(init?: Partial<HttpCriteria>) {
    Object.assign(this, init);
    this._filters = init?._filters || [];
    this._sort = init?._sort || [];
  }

  addFilter(filter: IFilter): HttpCriteria {
    this._filters.push(filter);
    return this;
  }

  removeFilter(index: number): HttpCriteria {
    if (index >= 0 && index < this._filters.length) {
      this._filters.splice(index, 1);
    }
    return this;
  }

  addSort(sort: ISort): HttpCriteria {
    this._sort.push(sort);
    return this;
  }

  changePerPage(perPage: number): HttpCriteria {
    this._perPage = perPage;
    return this;
  }

  toParams(): HttpParams {
    let params = new HttpParams();
    if (this._perPage != null) {
      params = params.set('_perPage', String(this._perPage));
    }
    if (this._page != null) {
      params = params.set('_page', String(this._page));
    }
    if (this._filters?.length) {
      params = params.set('_filters', JSON.stringify(this._filters));
    }
    if (this._sort?.length) {
      const sortItems = this._sort.filter(
        item => item?.field && String(item.field).trim()
      );
      if (sortItems.length) {
        params = params.set('_sort', JSON.stringify(sortItems));
      }
    }
    return params;
  }

  toHttpParams(): HttpParams {
    return this.toParams();
  }
}

@Injectable({ providedIn: 'root' })
export class SuperHttpClientProvider {
  private baseUrl: string;
  private domains: {
    [name: string]: { url: string; isDefault?: boolean };
  } = {};

  constructor(
    private http: HttpClient,
    @Optional() @Inject(SUPER_HTTP_CONFIG) config?: IRootConfig
  ) {
    this.domains = config?.domains || {};
    const def =
      Object.values(this.domains).find(d => d.isDefault) ||
      Object.values(this.domains)[0];
    this.baseUrl = def?.url || '';
  }

  private buildUrl(path: string): string {
    if (!this.baseUrl) {
      return path;
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.baseUrl}${path}`;
  }

  private requestOptions(options: any = {}): any {
    const headers = new HttpHeaders(options.headers || {});
    return { ...options, headers };
  }

  default = {
    get: <T = any>(url: string, options?: any): Observable<T> => {
      return this.http.get<T>(
        this.buildUrl(url),
        this.requestOptions(options) as any
      ) as any;
    },
    post: <T = any>(url: string, body: any, options?: any): Observable<T> => {
      return this.http.post<T>(
        this.buildUrl(url),
        body,
        this.requestOptions(options) as any
      ) as any;
    },
    put: <T = any>(url: string, body: any, options?: any): Observable<T> => {
      return this.http.put<T>(
        this.buildUrl(url),
        body,
        this.requestOptions(options) as any
      ) as any;
    },
    patch: <T = any>(url: string, body: any, options?: any): Observable<T> => {
      return this.http.patch<T>(
        this.buildUrl(url),
        body,
        this.requestOptions(options) as any
      ) as any;
    },
    delete: <T = any>(url: string, options?: any): Observable<T> => {
      return this.http.delete<T>(
        this.buildUrl(url),
        this.requestOptions(options) as any
      ) as any;
    },
    filter: <T = any>(
      url: string,
      criteria: HttpCriteria,
      options?: any
    ): Observable<T> => {
      const params = criteria?.toParams();
      const opts = this.requestOptions({ ...options, params });
      return this.http.get<T>(this.buildUrl(url), opts as any) as any;
    }
  };

  use(domain: string): this {
    const d = this.domains[domain];
    if (d?.url) {
      this.baseUrl = d.url;
    }
    return this;
  }

  get<T = any>(url: string, options?: any): Observable<T> {
    return this.default.get<T>(url, options);
  }

  post<T = any>(url: string, body: any, options?: any): Observable<T> {
    return this.default.post<T>(url, body, options);
  }

  put<T = any>(url: string, body: any, options?: any): Observable<T> {
    return this.default.put<T>(url, body, options);
  }

  patch<T = any>(url: string, body: any, options?: any): Observable<T> {
    return this.default.patch<T>(url, body, options);
  }

  delete<T = any>(url: string, options?: any): Observable<T> {
    return this.default.delete<T>(url, options);
  }

  filter<T = any>(
    url: string,
    criteria: HttpCriteria,
    options?: any
  ): Observable<T> {
    return this.default.filter<T>(url, criteria, options);
  }
}

@NgModule({})
export class SuperHttpClientModule {
  static forRoot(
    config: IRootConfig
  ): ModuleWithProviders<SuperHttpClientModule> {
    return {
      ngModule: SuperHttpClientModule,
      providers: [{ provide: SUPER_HTTP_CONFIG, useValue: config }]
    };
  }
}
